import type { DefaultPartialBlock } from "../types";

/**
 * BlockNote가 모델링하지 않는 표 셀 속성(verticalAlignment, rowHeight 등)을 블록 JSON에
 * 주입하는 직렬화 헬퍼.
 *
 * BlockNote의 nodeToBlock(contentNodeToTableContent)은 고정된 prop 집합
 * (colspan/rowspan/backgroundColor/textColor/textAlignment)만 cell.props에 담으므로,
 * 커스텀 글로벌 attribute는 블록 JSON에서 누락된다. 이 모듈은 ProseMirror 상태에서
 * 직접 읽어 cell.props에 다시 주입한다(onContentChange 직전).
 *
 * 로드(역방향)는 별도 코드가 필요 없다: BlockNote blockToNode가 `...cell.props`를 노드
 * attr로 그대로 펼치므로, 글로벌 attribute로 등록만 돼 있으면 자동 복원된다.
 */

/**
 * 각 셀 속성의 "기본값"(= 주입에서 제외할 값). 기본값이거나 null/undefined면 주입하지 않아
 * 블록 JSON을 불필요하게 키우지 않는다.
 */
const CELL_ATTR_DEFAULTS: Record<string, unknown> = {
  verticalAlignment: "top",
  rowHeight: null,
};

function isMeaningful(attr: string, value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  return value !== CELL_ATTR_DEFAULTS[attr];
}

type CellAttrs = {
  row: number;
  col: number;
  props: Record<string, unknown>;
};

/**
 * ProseMirror 문서에서 각 table 블록 id → 셀별(주입 대상 attr만 담은) props 맵을 구축한다.
 */
function buildTableCellAttrMap(
  doc: any,
  attrs: string[],
): Map<string, CellAttrs[]> {
  const result = new Map<string, CellAttrs[]>();

  doc.descendants((node: any) => {
    if (node.type.name === "blockContainer") {
      const blockId = node.attrs?.id;
      const contentNode = node.firstChild;
      if (blockId && contentNode?.type.name === "table") {
        const cells: CellAttrs[] = [];
        let rowIndex = 0;
        contentNode.forEach((rowNode: any) => {
          if (rowNode.type.name === "tableRow") {
            let colIndex = 0;
            rowNode.forEach((cellNode: any) => {
              const props: Record<string, unknown> = {};
              for (const attr of attrs) {
                const value = cellNode.attrs?.[attr];
                if (isMeaningful(attr, value)) {
                  props[attr] = value;
                }
              }
              if (Object.keys(props).length > 0) {
                cells.push({ row: rowIndex, col: colIndex, props });
              }
              colIndex++;
            });
            rowIndex++;
          }
        });
        if (cells.length > 0) {
          result.set(blockId, cells);
        }
      }
      return false;
    }
  });

  return result;
}

function patchBlocks(
  blocks: DefaultPartialBlock[],
  cellAttrMap: Map<string, CellAttrs[]>,
): DefaultPartialBlock[] {
  return blocks.map((block) => {
    if (block.type !== "table" || !block.id || !block.content) {
      if (block.children && block.children.length > 0) {
        return {
          ...block,
          children: patchBlocks(
            block.children as DefaultPartialBlock[],
            cellAttrMap,
          ),
        };
      }
      return block;
    }

    const cells = cellAttrMap.get(block.id);
    if (!cells || cells.length === 0) {
      return block;
    }

    const content = block.content as any;
    if (content.type !== "tableContent" || !content.rows) {
      return block;
    }

    const newRows = content.rows.map((row: any, rowIndex: number) => {
      const newCells = row.cells.map((cell: any, colIndex: number) => {
        const match = cells.find(
          (c) => c.row === rowIndex && c.col === colIndex,
        );
        if (!match) {
          return cell;
        }
        if (cell && typeof cell === "object" && cell.type === "tableCell") {
          return {
            ...cell,
            props: { ...cell.props, ...match.props },
          };
        }
        return cell;
      });
      return { ...row, cells: newCells };
    });

    return {
      ...block,
      content: { ...content, rows: newRows },
    };
  });
}

/**
 * blocks 배열의 table 블록을 찾아, ProseMirror 상태에서 읽은 셀별 attr(`attrs`로 지정)을
 * cell.props에 주입한다. 한 번의 문서 순회로 여러 attr을 동시에 처리한다.
 *
 * @param attrs 주입할 셀 노드 attr 이름들(예: ["verticalAlignment", "rowHeight"])
 */
export function injectTableCellAttrs(
  blocks: DefaultPartialBlock[],
  editor: any,
  attrs: string[],
): DefaultPartialBlock[] {
  const tiptap = editor?._tiptapEditor;
  if (!tiptap) {
    return blocks;
  }

  const { doc } = tiptap.state;
  const cellAttrMap = buildTableCellAttrMap(doc, attrs);
  if (cellAttrMap.size === 0) {
    return blocks;
  }

  return patchBlocks(blocks, cellAttrMap);
}

/**
 * verticalAlignment만 주입하는 하위호환 래퍼. 기존 호출부 호환을 위해 유지.
 * @deprecated 신규 호출부는 injectTableCellAttrs를 직접 사용.
 */
export function injectVerticalAlignment(
  blocks: DefaultPartialBlock[],
  editor: any,
): DefaultPartialBlock[] {
  return injectTableCellAttrs(blocks, editor, ["verticalAlignment"]);
}

/** table 노드(블록 레벨) 보존 대상 attr과 기본값. */
const TABLE_BLOCK_ATTR_DEFAULTS: Record<string, unknown> = {
  tableAlignment: "left",
};

/**
 * ProseMirror 문서에서 table 블록 id → 블록 레벨 attr(tableAlignment 등) 맵을 만든다.
 * 셀이 아니라 table 노드 자체의 attr을 읽는다.
 */
function buildTableBlockAttrMap(
  doc: any,
  attrs: string[],
): Map<string, Record<string, unknown>> {
  const result = new Map<string, Record<string, unknown>>();

  doc.descendants((node: any) => {
    if (node.type.name === "blockContainer") {
      const blockId = node.attrs?.id;
      const contentNode = node.firstChild;
      if (blockId && contentNode?.type.name === "table") {
        const props: Record<string, unknown> = {};
        for (const attr of attrs) {
          const value = contentNode.attrs?.[attr];
          if (
            value !== null &&
            value !== undefined &&
            value !== TABLE_BLOCK_ATTR_DEFAULTS[attr]
          ) {
            props[attr] = value;
          }
        }
        if (Object.keys(props).length > 0) {
          result.set(blockId, props);
        }
      }
      return false;
    }
    return undefined;
  });

  return result;
}

function patchTableBlockProps(
  blocks: DefaultPartialBlock[],
  attrMap: Map<string, Record<string, unknown>>,
): DefaultPartialBlock[] {
  return blocks.map((block) => {
    if (block.type === "table" && block.id && attrMap.has(block.id)) {
      return {
        ...block,
        props: { ...(block.props as any), ...attrMap.get(block.id) },
      } as DefaultPartialBlock;
    }
    if (block.children && block.children.length > 0) {
      return {
        ...block,
        children: patchTableBlockProps(
          block.children as DefaultPartialBlock[],
          attrMap,
        ),
      };
    }
    return block;
  });
}

/**
 * blocks 배열의 table 블록에, ProseMirror 상태에서 읽은 table 노드 블록 레벨 attr
 * (예: tableAlignment)을 block.props에 주입한다. BlockNote nodeToBlock이 table 노드의
 * 커스텀 attr을 block.props로 추출하지 않으므로 직접 주입한다.
 */
export function injectTableBlockAttrs(
  blocks: DefaultPartialBlock[],
  editor: any,
): DefaultPartialBlock[] {
  const tiptap = editor?._tiptapEditor;
  if (!tiptap) {
    return blocks;
  }
  const { doc } = tiptap.state;
  const attrMap = buildTableBlockAttrMap(doc, ["tableAlignment"]);
  if (attrMap.size === 0) {
    return blocks;
  }
  return patchTableBlockProps(blocks, attrMap);
}
